import axios, {
  AxiosInstance,
  AxiosError,
  InternalAxiosRequestConfig,
} from 'axios';
import { apiConfig } from '@/config/api';

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: apiConfig.baseURL,
  timeout: apiConfig.timeout,
  headers: apiConfig.headers,
  withCredentials: true, // Enable sending cookies (required for HTTP-only cookies with HMAC signature)
  // maxRedirects: 5 is default, but we'll handle 307 redirects manually
  // validateStatus: default throws on 4xx/5xx, which is what we want
});

// Request interceptor
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Add auth token if available
    const token = localStorage.getItem('authToken');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      console.warn('[API Request] No auth token found in localStorage');
    }

    // For FormData, remove Content-Type header to let Axios set it automatically with boundary
    if (config.data instanceof FormData && config.headers) {
      delete config.headers['Content-Type'];
    }

    // Handle trailing slash issues - if URL doesn't end with /, ensure it doesn't redirect
    // FastAPI often redirects /users to /users/ or vice versa
    if (config.url && !config.url.includes('?') && !config.url.endsWith('/')) {
      // For POST/PUT/DELETE, we want to keep the URL as-is to avoid redirects
      // But if we get a redirect, we'll handle it
    }

    // Log request in development with full details
    if (import.meta.env.DEV) {
      let dataStr = '(empty)';
      let dataKeys: string[] = [];
      let dataValues: any[] = [];
      
      if (config.data) {
        if (config.data instanceof FormData) {
          // Handle FormData specially
          dataStr = 'FormData';
          const entries: string[] = [];
          for (const [key, value] of config.data.entries()) {
            entries.push(`${key}: ${value}`);
            dataKeys.push(key);
            dataValues.push(value);
          }
          dataStr = `FormData { ${entries.join(', ')} }`;
        } else if (typeof config.data === 'object') {
          dataStr = JSON.stringify(config.data, null, 2);
          dataKeys = Object.keys(config.data);
          dataValues = Object.values(config.data);
        } else {
          dataStr = String(config.data);
        }
      }
      
      const fullURL = `${config.baseURL || ''}${config.url}`;
      const isUsingProxy = !config.baseURL && config.url?.startsWith('/api');
      
      console.log(
        `[API Request] ${config.method?.toUpperCase()} ${config.url}`,
        {
          data: config.data || '(NO DATA!)',
          dataString: dataStr,
          dataType: typeof config.data,
          isFormData: config.data instanceof FormData,
          dataKeys: dataKeys,
          dataValues: dataValues,
          headers: {
            Authorization: config.headers?.Authorization ? 'Bearer ***' : 'NOT SET',
            'Content-Type': config.headers?.['Content-Type'] || 'NOT SET (will be set by Axios)',
            ...(config.data instanceof FormData ? { 'Note': 'FormData - Content-Type will be set automatically with boundary' } : {}),
          },
          withCredentials: config.withCredentials,
          baseURL: config.baseURL || '(empty - using proxy)',
          fullURL: fullURL,
          usingProxy: isUsingProxy,
          note: isUsingProxy ? 'Request will be proxied to http://localhost:8000' : 'Direct request to backend',
        }
      );
    }

    return config;
  },
  (error: AxiosError) => {
    console.error('[API Request Error]', error);
    return Promise.reject(error);
  }
);

// Response interceptor
apiClient.interceptors.response.use(
  (response) => {
    // Log response in development
    if (import.meta.env.DEV) {
      console.log(
        `[API Response] ${response.config.method?.toUpperCase()} ${response.config.url}`,
        response.data
      );
    }
    return response;
  },
  (error: AxiosError) => {
    // Enhanced error handling
    if (error.response) {
      // Server responded with error status
      console.error('[API Response Error]', {
        status: error.response.status,
        data: error.response.data,
        url: error.config?.url,
      });

      // Handle specific error statuses
      switch (error.response.status) {
        case 401:
          // Unauthorized - redirect to login if needed
          localStorage.removeItem('authToken');
          // window.location.href = '/login';
          break;
        case 403:
          // Forbidden
          console.error('Access forbidden');
          break;
        case 404:
          // Not found
          console.error('Resource not found');
          break;
        case 405:
          // Method Not Allowed - usually means authentication issue or wrong endpoint
          console.error('Method not allowed - check authentication and endpoint');
          break;
        case 422:
          // Validation error - handled by form
          break;
        case 500:
          // Server error
          console.error('Server error occurred');
          break;
      }
    } else if (error.request) {
      // Request made but no response received
      console.error('[API Network Error]', {
        message: error.message,
        request: error.request,
        code: error.code,
        config: {
          url: error.config?.url,
          baseURL: error.config?.baseURL,
          method: error.config?.method,
        },
      });
    } else {
      // Error in request configuration
      console.error('[API Configuration Error]', {
        message: error.message,
        config: error.config,
      });
    }

    return Promise.reject(error);
  }
);

export default apiClient;
